/**
 * Typed admin API client (build plan P2-T1). One method per OpenAPI operation,
 * shapes from `@attendik/types/admin-schemas`.
 *
 * Two modes, selected by `VITE_ADMIN_API_MODE`:
 *   - "mock"  (default) — dispatches to the in-app fixture layer (src/api/mock.ts).
 *   - "real"            — issues fetch() calls against VITE_ADMIN_API_BASE_URL.
 *
 * In both modes the client throws `ApiError` (or `ScopeError` on 403) so the
 * UI handles failure identically regardless of mode.
 */
import type {
  AdminUser,
  AssignedOperator,
  Attendee,
  AttendeePage,
  AuditPage,
  CreateStageRequest,
  Customer,
  CustomerStatus,
  DashboardSummary,
  Direction,
  EmailScanLogResponse,
  Event,
  EventStatus,
  InviteAdminUserResponse,
  LiveCounts,
  LoginResponse,
  Me,
  MeetpointConfig,
  MeetpointConfigInput,
  MeetpointEventSummary,
  MeetpointTestResponse,
  Operator,
  ScanLogResultCode,
  ScanPage,
  Stage,
  StageOperator,
  SyncRun,
  SyncRunPage,
  TestWebhookResponse,
  UpdateStageRequest,
  WebhookSecretResponse
} from "@attendik/types/admin-schemas";

import { ApiError, ScopeError } from "./errors.js";
import { mockApi, type MockApi } from "./mock.js";

/**
 * Monitor CR-02: one recipient of the per-event Snapshot email.
 * Mirrors the admin-api `event_snapshot_recipients` row shape.
 */
export interface SnapshotRecipient {
  id: string;
  email: string;
  created_at: string;
  created_by: string | null;
}

const TOKEN_KEY = "attendik.admin.access_token";
const REFRESH_KEY = "attendik.admin.refresh_token";
const BASE_URL = import.meta.env.VITE_ADMIN_API_BASE_URL ?? "";
const API_PREFIX = "/admin/v1";

const MODE: "mock" | "real" = import.meta.env.VITE_ADMIN_API_MODE === "real" ? "real" : "mock";

export function isMockMode(): boolean {
  return MODE === "mock";
}

// ---- Token storage ----------------------------------------------------------

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_KEY, token);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

// ---- Real-mode fetch transport ---------------------------------------------

interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean;
  query?: Record<string, string | number | undefined>;
}

function buildQuery(query?: Record<string, string | number | undefined>): string {
  if (!query) {
    return "";
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/**
 * Sends the browser to /login after a session ends. Wrapped in try/catch so
 * environments without navigation support (jsdom in tests) do not surface a
 * spurious error — the redirect is a side effect, not part of the contract.
 */
function redirectToLogin(): void {
  if (window.location.pathname === "/login") {
    return;
  }
  try {
    window.location.assign("/login");
  } catch {
    // jsdom does not implement navigation — ignore.
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, auth = true, query } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (auth) {
    const token = getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${API_PREFIX}${path}${buildQuery(query)}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body)
    });
  } catch {
    throw new ApiError(0, "NETWORK", "Could not connect. Check your connection.");
  }

  if (response.status === 401 && auth) {
    clearToken();
    redirectToLogin();
    throw new ApiError(401, "UNAUTHORIZED", "Your session has expired.");
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const envelope = payload as
      | { error?: string; code?: string; conflicts?: unknown; sync_log_id?: string }
      | null;
    const code = envelope?.code ?? "ERROR";
    const message = envelope?.error ?? "Request failed";
    if (response.status === 403) {
      throw new ScopeError(message);
    }
    const err = new ApiError(response.status, code, message);
    if (code === "OVERLAP" && Array.isArray(envelope?.conflicts)) {
      err.conflicts = envelope.conflicts as ApiError["conflicts"];
    }
    if (code === "SYNC_IN_PROGRESS" && typeof envelope?.sync_log_id === "string") {
      err.syncLogId = envelope.sync_log_id;
    }
    throw err;
  }

  return payload as T;
}

// ---- Mock-mode dispatch -----------------------------------------------------
// Re-maps ApiError 403s to ScopeError so the UI behaviour matches real mode.

async function viaMock<T>(fn: (api: MockApi, token: string | null) => Promise<T>): Promise<T> {
  try {
    return await fn(mockApi, getToken());
  } catch (err) {
    if (err instanceof ApiError && err.status === 403 && !(err instanceof ScopeError)) {
      const scoped = new ScopeError(err.message);
      throw scoped;
    }
    if (err instanceof ApiError && err.status === 401) {
      clearToken();
      redirectToLogin();
    }
    throw err;
  }
}

// ---- Filter argument shapes -------------------------------------------------

export interface EventFilters {
  customer_id?: string;
  status?: EventStatus;
}

export type AttendeeSortColumn =
  | "name"
  | "email"
  | "company"
  | "source"
  | "status";

export interface AttendeeFilters {
  source?: Attendee["source"];
  status?: Attendee["status"];
  /** Free-text; whitespace tokens AND-ed across name, email, company. */
  q?: string;
  sort?: AttendeeSortColumn;
  dir?: "asc" | "desc";
  page?: number;
  limit?: number;
}

export interface ScanFilters {
  operator_id?: string;
  direction?: Direction;
  result_code?: ScanLogResultCode;
  page?: number;
  limit?: number;
  // CR-11. Default (false/omitted) shows the live run only; true reveals
  // pre-go-live test scans (the "show test-period scans" toggle).
  include_test?: boolean;
}

export interface AuditFilters {
  admin_user_id?: string;
  entity_type?: string;
  customer_id?: string;
  page?: number;
  limit?: number;
}

// ---- The client ------------------------------------------------------------

class ApiClient {
  // ---- Auth ----
  async login(email: string, password: string): Promise<LoginResponse> {
    if (MODE === "mock") {
      return mockApi.login(email, password);
    }
    return request<LoginResponse>("/auth/login", {
      method: "POST",
      body: { email, password },
      auth: false
    });
  }

  async logout(): Promise<void> {
    const refreshToken = getRefreshToken();
    if (MODE === "mock") {
      await mockApi.logout();
      return;
    }
    await request<void>("/auth/logout", {
      method: "POST",
      body: { refresh_token: refreshToken ?? "" }
    });
  }

  async getMe(): Promise<Me> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.getMe(token));
    }
    return request<Me>("/me");
  }

  // ---- Customers ----
  async listCustomers(status?: CustomerStatus): Promise<Customer[]> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.listCustomers(token, status));
    }
    return request<Customer[]>("/customers", { query: { status } });
  }

  async createCustomer(name: string, slug: string): Promise<Customer> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.createCustomer(token, { name, slug }));
    }
    return request<Customer>("/customers", { method: "POST", body: { name, slug } });
  }

  async getCustomer(id: string): Promise<Customer> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.getCustomer(token, id));
    }
    return request<Customer>(`/customers/${id}`);
  }

  async updateCustomer(
    id: string,
    body: { name?: string; status?: CustomerStatus }
  ): Promise<Customer> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.updateCustomer(token, id, body));
    }
    return request<Customer>(`/customers/${id}`, { method: "PATCH", body });
  }

  // ---- Admin users ----
  async listAdminUsers(): Promise<AdminUser[]> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.listAdminUsers(token));
    }
    return request<AdminUser[]>("/admin-users");
  }

  async createAdminUser(body: {
    email: string;
    name: string;
    role: AdminUser["role"];
    customer_id?: string | null;
    password: string;
  }): Promise<AdminUser> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.createAdminUser(token, body));
    }
    return request<AdminUser>("/admin-users", { method: "POST", body });
  }

  async inviteAdminUser(body: {
    email: string;
    name: string;
    role: AdminUser["role"];
    customer_id?: string | null;
  }): Promise<InviteAdminUserResponse> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.inviteAdminUser(token, body));
    }
    return request<InviteAdminUserResponse>("/admin-users/invite", {
      method: "POST",
      body
    });
  }

  async updateAdminUser(
    id: string,
    body: { name?: string; status?: AdminUser["status"] }
  ): Promise<AdminUser> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.updateAdminUser(token, id, body));
    }
    return request<AdminUser>(`/admin-users/${id}`, { method: "PATCH", body });
  }

  async resetAdminUserPassword(id: string, newPassword: string): Promise<void> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.resetAdminUserPassword(token, id, newPassword));
    }
    return request<void>(`/admin-users/${id}/reset-password`, {
      method: "POST",
      body: { new_password: newPassword }
    });
  }

  // ---- Events ----
  async listEvents(filters: EventFilters = {}): Promise<Event[]> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.listEvents(token, filters));
    }
    return request<Event[]>("/events", { query: { ...filters } });
  }

  async createEvent(body: {
    customer_id: string;
    name: string;
    active_from: string;
    active_to: string;
    capacity?: number | null;
    webhook_url?: string | null;
  }): Promise<Event> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.createEvent(token, body));
    }
    return request<Event>("/events", { method: "POST", body });
  }

  async getEvent(id: string): Promise<Event> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.getEvent(token, id));
    }
    return request<Event>(`/events/${id}`);
  }

  async updateEvent(
    id: string,
    body: {
      name?: string;
      active_from?: string;
      active_to?: string;
      status?: EventStatus;
      capacity?: number | null;
      webhook_url?: string | null;
      // CR-11. Clear with null ("back to test mode") or set an explicit cutoff.
      went_live_at?: string | null;
    }
  ): Promise<Event> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.updateEvent(token, id, body));
    }
    return request<Event>(`/events/${id}`, { method: "PATCH", body });
  }

  async rotateWebhookSecret(id: string): Promise<WebhookSecretResponse> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.rotateWebhookSecret(token, id));
    }
    return request<WebhookSecretResponse>(`/events/${id}/rotate-webhook-secret`, {
      method: "POST"
    });
  }

  // CR-11. Marks the event live as of now() (resets every live counter). The
  // updated event carries the new went_live_at. A moving cutoff — calling it
  // again re-zeroes from the new instant.
  async goLive(id: string): Promise<Event> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.goLive(token, id));
    }
    return request<Event>(`/events/${id}/go-live`, { method: "POST" });
  }

  async testWebhook(id: string): Promise<TestWebhookResponse> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.testWebhook(token, id));
    }
    return request<TestWebhookResponse>(`/events/${id}/test-webhook`, { method: "POST" });
  }

  /** Live attendance counts for an event — powers the Dashboard live strip. */
  async getEventLiveCounts(id: string): Promise<LiveCounts> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.getEventLiveCounts(token, id));
    }
    return request<LiveCounts>(`/events/${id}/live-counts`);
  }

  /** Public, HMAC-signed monitor URL — opens the attendik-monitor SPA. */
  async getEventMonitorLink(id: string): Promise<{ url: string }> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.getEventMonitorLink(token, id));
    }
    return request<{ url: string }>(`/events/${id}/monitor-link`);
  }

  // ---- Monitor CR-02: per-event snapshot recipient list ----
  async listSnapshotRecipients(
    id: string
  ): Promise<{ data: SnapshotRecipient[] }> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.listSnapshotRecipients(token, id));
    }
    return request<{ data: SnapshotRecipient[] }>(
      `/events/${id}/snapshot-recipients`
    );
  }

  async setSnapshotRecipients(
    id: string,
    emails: string[]
  ): Promise<{ data: SnapshotRecipient[] }> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.setSnapshotRecipients(token, id, emails));
    }
    return request<{ data: SnapshotRecipient[] }>(
      `/events/${id}/snapshot-recipients`,
      { method: "PUT", body: { emails } }
    );
  }

  // ---- Operators ----
  async listOperators(customerId?: string): Promise<Operator[]> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.listOperators(token, customerId));
    }
    return request<Operator[]>("/operators", { query: { customer_id: customerId } });
  }

  async createOperator(body: {
    customer_id: string;
    email: string;
    name: string;
    password: string;
  }): Promise<Operator> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.createOperator(token, body));
    }
    return request<Operator>("/operators", { method: "POST", body });
  }

  async getOperator(id: string): Promise<Operator> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.getOperator(token, id));
    }
    return request<Operator>(`/operators/${id}`);
  }

  async updateOperator(
    id: string,
    body: { name?: string; status?: Operator["status"] }
  ): Promise<Operator> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.updateOperator(token, id, body));
    }
    return request<Operator>(`/operators/${id}`, { method: "PATCH", body });
  }

  async resetOperatorPassword(id: string, newPassword: string): Promise<void> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.resetOperatorPassword(token, id, newPassword));
    }
    return request<void>(`/operators/${id}/reset-password`, {
      method: "POST",
      body: { new_password: newPassword }
    });
  }

  // ---- Event operators ----
  async listEventOperators(eventId: string): Promise<AssignedOperator[]> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.listEventOperators(token, eventId));
    }
    return request<AssignedOperator[]>(`/events/${eventId}/operators`);
  }

  async assignEventOperator(
    eventId: string,
    operatorId: string
  ): Promise<{ event_id: string; operator_id: string; created_at: string }> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.assignEventOperator(token, eventId, operatorId));
    }
    return request(`/events/${eventId}/operators`, {
      method: "POST",
      body: { operator_id: operatorId }
    });
  }

  async unassignEventOperator(eventId: string, operatorId: string): Promise<void> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.unassignEventOperator(token, eventId, operatorId));
    }
    return request<void>(`/events/${eventId}/operators/${operatorId}`, { method: "DELETE" });
  }

  // ---- Stages (admin CR-01) ----
  async listStages(eventId: string): Promise<Stage[]> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.listStages(token, eventId));
    }
    return request<Stage[]>(`/events/${eventId}/stages`);
  }

  async createStage(eventId: string, body: CreateStageRequest): Promise<Stage> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.createStage(token, eventId, body));
    }
    return request<Stage>(`/events/${eventId}/stages`, { method: "POST", body });
  }

  async getStage(stageId: string): Promise<Stage> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.getStage(token, stageId));
    }
    return request<Stage>(`/stages/${stageId}`);
  }

  async updateStage(stageId: string, body: UpdateStageRequest): Promise<Stage> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.updateStage(token, stageId, body));
    }
    return request<Stage>(`/stages/${stageId}`, { method: "PATCH", body });
  }

  async deleteStage(stageId: string): Promise<void> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.deleteStage(token, stageId));
    }
    return request<void>(`/stages/${stageId}`, { method: "DELETE" });
  }

  async getStageMonitorLink(stageId: string): Promise<{ url: string }> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.getStageMonitorLink(token, stageId));
    }
    return request<{ url: string }>(`/stages/${stageId}/monitor-link`);
  }

  // CR-06: convert a stage from `meetpoint` to `manual` so the next sync
  // skips it. The reverse direction is rejected server-side.
  async convertStageSource(stageId: string, source: "manual"): Promise<Stage> {
    if (MODE === "mock") {
      return viaMock((api, token) =>
        api.convertStageSource(token, stageId, source)
      );
    }
    return request<Stage>(`/stages/${stageId}/source`, {
      method: "PATCH",
      body: { source }
    });
  }

  async listStageOperators(stageId: string): Promise<Operator[]> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.listStageOperators(token, stageId));
    }
    return request<Operator[]>(`/stages/${stageId}/operators`);
  }

  async assignStageOperator(
    stageId: string,
    operatorId: string
  ): Promise<StageOperator> {
    if (MODE === "mock") {
      return viaMock((api, token) =>
        api.assignStageOperator(token, stageId, operatorId)
      );
    }
    return request<StageOperator>(`/stages/${stageId}/operators`, {
      method: "POST",
      body: { operator_id: operatorId }
    });
  }

  async unassignStageOperator(
    stageId: string,
    operatorId: string
  ): Promise<void> {
    if (MODE === "mock") {
      return viaMock((api, token) =>
        api.unassignStageOperator(token, stageId, operatorId)
      );
    }
    return request<void>(`/stages/${stageId}/operators/${operatorId}`, {
      method: "DELETE"
    });
  }

  // ---- Attendees ----
  async listAttendees(eventId: string, filters: AttendeeFilters = {}): Promise<AttendeePage> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.listAttendees(token, eventId, filters));
    }
    return request<AttendeePage>(`/events/${eventId}/attendees`, { query: { ...filters } });
  }

  async createAttendee(
    eventId: string,
    body: {
      name: string;
      email?: string | null;
      company?: string | null;
      // CR-07. Free-form group / category labels.
      group_names?: string[];
    }
  ): Promise<Attendee> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.createAttendee(token, eventId, body));
    }
    return request<Attendee>(`/events/${eventId}/attendees`, { method: "POST", body });
  }

  async updateAttendee(
    eventId: string,
    attendeeId: string,
    body: {
      name?: string;
      email?: string | null;
      company?: string | null;
      // CR-07. Pass `[]` to clear, omit to leave alone.
      group_names?: string[];
    }
  ): Promise<Attendee> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.updateAttendee(token, eventId, attendeeId, body));
    }
    return request<Attendee>(`/events/${eventId}/attendees/${attendeeId}`, {
      method: "PATCH",
      body
    });
  }

  async deleteAttendee(eventId: string, attendeeId: string): Promise<void> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.deleteAttendee(token, eventId, attendeeId));
    }
    return request<void>(`/events/${eventId}/attendees/${attendeeId}`, { method: "DELETE" });
  }

  // ---- Scans ----
  async listScans(eventId: string, filters: ScanFilters = {}): Promise<ScanPage> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.listScans(token, eventId, filters));
    }
    return request<ScanPage>(`/events/${eventId}/scans`, {
      query: {
        operator_id: filters.operator_id,
        direction: filters.direction,
        result_code: filters.result_code ?? undefined,
        page: filters.page,
        limit: filters.limit,
        include_test: filters.include_test ? "true" : undefined
      }
    });
  }

  async emailScanLog(
    eventId: string,
    recipients: string[]
  ): Promise<EmailScanLogResponse> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.emailScanLog(token, eventId, recipients));
    }
    return request<EmailScanLogResponse>(`/events/${eventId}/scan-log/email`, {
      method: "POST",
      body: { recipients }
    });
  }

  // ---- Meetpoint ----
  async getMeetpointConfig(eventId: string): Promise<MeetpointConfig> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.getMeetpointConfig(token, eventId));
    }
    return request<MeetpointConfig>(`/events/${eventId}/meetpoint`);
  }

  async putMeetpointConfig(
    eventId: string,
    body: MeetpointConfigInput
  ): Promise<MeetpointConfig> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.putMeetpointConfig(token, eventId, body));
    }
    return request<MeetpointConfig>(`/events/${eventId}/meetpoint`, { method: "PUT", body });
  }

  async deleteMeetpointConfig(eventId: string): Promise<void> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.deleteMeetpointConfig(token, eventId));
    }
    return request<void>(`/events/${eventId}/meetpoint`, { method: "DELETE" });
  }

  async testMeetpointConnection(eventId: string): Promise<MeetpointTestResponse> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.testMeetpointConnection(token, eventId));
    }
    return request<MeetpointTestResponse>(`/events/${eventId}/meetpoint/test`, {
      method: "POST"
    });
  }

  // CR-02: verify credentials and list the events the operator can see in
  // Meetpoint. Used by the Integration tab's "Connect & pick event"
  // helper so the user doesn't have to type the numeric EventID.
  async connectMeetpoint(
    eventId: string,
    body: {
      base_url?: string;
      username: string;
      password: string;
      language_id?: string;
    }
  ): Promise<{
    events: {
      event_id: string;
      name: string;
      start_at: string | null;
      end_at: string | null;
    }[];
  }> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.connectMeetpoint(token, eventId, body));
    }
    return request<{
      events: {
        event_id: string;
        name: string;
        start_at: string | null;
        end_at: string | null;
      }[];
    }>(`/events/${eventId}/meetpoint/connect`, {
      method: "POST",
      body
    });
  }

  // CR-06: pre-fill the event name + active window from MP.
  async getMeetpointEventSummary(
    eventId: string
  ): Promise<MeetpointEventSummary> {
    if (MODE === "mock") {
      return viaMock((api, token) =>
        api.getMeetpointEventSummary(token, eventId)
      );
    }
    return request<MeetpointEventSummary>(
      `/events/${eventId}/meetpoint/event-summary`
    );
  }

  async applyMeetpointSummary(eventId: string): Promise<Event> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.applyMeetpointSummary(token, eventId));
    }
    return request<Event>(`/events/${eventId}/meetpoint/apply-summary`, {
      method: "POST"
    });
  }

  // ---- Meetpoint sync ----
  async listSyncRuns(eventId: string, page = 1, limit = 50): Promise<SyncRunPage> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.listSyncRuns(token, eventId, page, limit));
    }
    return request<SyncRunPage>(`/events/${eventId}/meetpoint/sync`, {
      query: { page, limit }
    });
  }

  async triggerSync(
    eventId: string,
    cleanMissingAttendees = false
  ): Promise<{ sync_log_id: string; deleted_count: number }> {
    if (MODE === "mock") {
      return viaMock((api, token) =>
        api.triggerSync(token, eventId, cleanMissingAttendees)
      );
    }
    return request<{ sync_log_id: string; deleted_count: number }>(`/events/${eventId}/meetpoint/sync`, {
      method: "POST",
      body: cleanMissingAttendees
        ? { clean_missing_attendees: true, confirm: "DELETE_MISSING_MEETPOINT_ATTENDEES" }
        : undefined
    });
  }

  async getSyncRun(id: string): Promise<SyncRun> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.getSyncRun(token, id));
    }
    return request<SyncRun>(`/sync-runs/${id}`);
  }

  // ---- Dashboard ----
  /** One scoped payload powering the dashboard — counts, attention, recents. */
  async getDashboard(): Promise<DashboardSummary> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.getDashboard(token));
    }
    return request<DashboardSummary>("/dashboard");
  }

  // ---- Audit ----
  async listAudit(filters: AuditFilters = {}): Promise<AuditPage> {
    if (MODE === "mock") {
      return viaMock((api, token) => api.listAudit(token, filters));
    }
    return request<AuditPage>("/audit", { query: { ...filters } });
  }
}

export const api = new ApiClient();
